import { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Link2,
  Plus,
  Search,
  Pencil,
  Trash2,
  XCircle,
  ChevronLeft,
  ChevronRight,
  Loader2,
} from 'lucide-react';
import { api } from '@/lib/api';

// ─── Types ────────────────────────────────────────────────────────────────────

type AssignmentStatus = 'confirmed' | 'cancelled' | 'deleted';

interface Assignment {
  id: number;
  participantId: number;
  participantName: string;
  equipmentId: number;
  equipmentName: string;
  startedAt: string;
  endedAt?: string;
  status: AssignmentStatus;
  createdAt: string;
}

interface AssignmentInput {
  participantId: number | '';
  equipmentId: number | '';
  startedAt: string;
  endedAt: string;
  status: AssignmentStatus;
}

interface ParticipantOption { id: number; name: string }
interface EquipmentOption  { id: number; name: string }

interface ListResponse {
  assignments: Assignment[] | null;
  total: number;
  limit: number;
  offset: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const STATUS_LABELS: Record<AssignmentStatus, string> = {
  confirmed: 'Confirmado',
  cancelled: 'Cancelado',
  deleted: 'Eliminado',
};

const STATUS_CLASSES: Record<AssignmentStatus, string> = {
  confirmed: 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400',
  cancelled: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400',
  deleted: 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400',
};

function fmt(iso?: string) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('pt-PT', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function toDatetimeLocal(iso?: string): string {
  if (!iso) return '';
  // Converts "2024-01-15T10:00:00Z" → "2024-01-15T10:00" for <input type="datetime-local">
  return iso.slice(0, 16);
}

function fromDatetimeLocal(local: string): string {
  if (!local) return '';
  return new Date(local).toISOString();
}

const PAGE_SIZE = 20;

const EMPTY_INPUT: AssignmentInput = {
  participantId: '',
  equipmentId: '',
  startedAt: '',
  endedAt: '',
  status: 'confirmed',
};

// ─── Modal ────────────────────────────────────────────────────────────────────

interface ModalProps {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}

function Modal({ title, onClose, children }: ModalProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-card rounded-xl shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="text-base font-semibold text-foreground">{title}</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">✕</button>
        </div>
        <div className="px-6 py-5">{children}</div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function EquipmentAssignmentsPage() {
  // List state
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Filters
  const [filterParticipant, setFilterParticipant] = useState('');
  const [filterEquipment, setFilterEquipment] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');

  // Select options
  const [participants, setParticipants] = useState<ParticipantOption[]>([]);
  const [equipments, setEquipments] = useState<EquipmentOption[]>([]);

  // Modal state
  const [showCreate, setShowCreate]   = useState(false);
  const [editTarget, setEditTarget]   = useState<Assignment | null>(null);
  const [cancelTarget, setCancelTarget] = useState<Assignment | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Assignment | null>(null);

  // Form state
  const [form, setForm] = useState<AssignmentInput>(EMPTY_INPUT);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // ── Fetch options (participants + equipments for dropdowns) ──────────────
  useEffect(() => {
    api.get<{ participants: { id: number; nome: string; apelido: string }[]; total: number }>(
      '/api/participants?limit=100&offset=0'
    )
      .then(res => {
        setParticipants(
          (res.participants ?? []).map(p => ({ id: p.id, name: `${p.nome} ${p.apelido}` }))
        );
      })
      .catch(() => {/* non-fatal */});

    api.get<{ equipments: { id: number; equipamento: string }[]; total: number }>(
      '/api/equipments?limit=200&offset=0'
    )
      .then(res => {
        setEquipments(
          (res.equipments ?? []).map(e => ({ id: e.id, name: e.equipamento }))
        );
      })
      .catch(() => {/* non-fatal */});
  }, []);

  // ── Fetch list ───────────────────────────────────────────────────────────
  const fetchList = useCallback(async (p: number) => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        limit: String(PAGE_SIZE),
        offset: String(p * PAGE_SIZE),
      });
      if (filterParticipant) params.set('participantId', filterParticipant);
      if (filterEquipment)   params.set('equipmentId', filterEquipment);
      if (filterStatus)      params.set('status', filterStatus);
      if (filterDateFrom)    params.set('dateFrom', fromDatetimeLocal(filterDateFrom));
      if (filterDateTo)      params.set('dateTo', fromDatetimeLocal(filterDateTo));

      const res = await api.get<ListResponse>(`/api/participant-equipment-assignments?${params}`);
      setAssignments(res.assignments ?? []);
      setTotal(res.total);
    } catch {
      setError('Não foi possível carregar os registos.');
    } finally {
      setLoading(false);
    }
  }, [filterParticipant, filterEquipment, filterStatus, filterDateFrom, filterDateTo]);

  useEffect(() => {
    setPage(0);
    fetchList(0);
  }, [fetchList]);

  // ── Form helpers ─────────────────────────────────────────────────────────
  function openCreate() {
    setForm(EMPTY_INPUT);
    setFormErrors({});
    setSubmitError('');
    setShowCreate(true);
  }

  function openEdit(a: Assignment) {
    setForm({
      participantId: a.participantId,
      equipmentId:   a.equipmentId,
      startedAt:     toDatetimeLocal(a.startedAt),
      endedAt:       toDatetimeLocal(a.endedAt),
      status:        a.status,
    });
    setFormErrors({});
    setSubmitError('');
    setEditTarget(a);
  }

  function handleFormChange(field: keyof AssignmentInput, value: string | number) {
    setForm(prev => ({ ...prev, [field]: value }));
    setFormErrors(prev => { const e = { ...prev }; delete e[field]; return e; });
  }

  async function handleCreate() {
    setSubmitting(true);
    setSubmitError('');
    try {
      await api.post('/api/participant-equipment-assignments', {
        participantId: Number(form.participantId),
        equipmentId:   Number(form.equipmentId),
        startedAt:     fromDatetimeLocal(form.startedAt),
        endedAt:       form.endedAt ? fromDatetimeLocal(form.endedAt) : undefined,
        status:        form.status,
      });
      setShowCreate(false);
      fetchList(page);
    } catch (err: unknown) {
      const e = err as { errors?: Record<string, string>; message?: string };
      if (e?.errors) {
        setFormErrors(e.errors);
      } else {
        setSubmitError(e?.message ?? 'Erro ao criar registo.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function handleUpdate() {
    if (!editTarget) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      await api.put(`/api/participant-equipment-assignments/${editTarget.id}`, {
        participantId: Number(form.participantId),
        equipmentId:   Number(form.equipmentId),
        startedAt:     fromDatetimeLocal(form.startedAt),
        endedAt:       form.endedAt ? fromDatetimeLocal(form.endedAt) : undefined,
        status:        form.status,
      });
      setEditTarget(null);
      fetchList(page);
    } catch (err: unknown) {
      const e = err as { errors?: Record<string, string>; message?: string };
      if (e?.errors) {
        setFormErrors(e.errors);
      } else {
        setSubmitError(e?.message ?? 'Erro ao guardar alterações.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function handleCancel() {
    if (!cancelTarget) return;
    setSubmitting(true);
    try {
      await api.post(`/api/participant-equipment-assignments/${cancelTarget.id}/cancel`, {});
      setCancelTarget(null);
      fetchList(page);
    } catch {
      /* error handled below */
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    setSubmitting(true);
    try {
      await api.delete(`/api/participant-equipment-assignments/${deleteTarget.id}`);
      setDeleteTarget(null);
      fetchList(page);
    } catch {
      /* error handled below */
    } finally {
      setSubmitting(false);
    }
  }

  const totalPages = Math.ceil(total / PAGE_SIZE);

  // ─── Render ──────────────────────────────────────────────────────────────

  return (
    <>
      <Helmet>
        <title>Equipamentos utilizados | DIGGER</title>
      </Helmet>

      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <Link2 className="w-6 h-6 text-primary" />
              Equipamentos utilizados
            </h1>
            <p className="text-muted-foreground mt-1 text-sm">
              Consulta de utilização dos equipamentos.
            </p>
          </div>
          <button
            onClick={openCreate}
            className="btn-primary flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Nova Associação
          </button>
        </div>

        {/* Filters */}
        <div className="card p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div>
            <label className="label mb-1">Participante</label>
            <select
              className="input"
              value={filterParticipant}
              onChange={e => setFilterParticipant(e.target.value)}
            >
              <option value="">Todos</option>
              {participants.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label mb-1">Equipamento</label>
            <select
              className="input"
              value={filterEquipment}
              onChange={e => setFilterEquipment(e.target.value)}
            >
              <option value="">Todos</option>
              {equipments.map(e => (
                <option key={e.id} value={e.id}>{e.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label mb-1">Estado</label>
            <select
              className="input"
              value={filterStatus}
              onChange={e => setFilterStatus(e.target.value)}
            >
              <option value="">Todos</option>
              <option value="confirmed">Confirmado</option>
              <option value="cancelled">Cancelado</option>
              <option value="deleted">Eliminado</option>
            </select>
          </div>
          <div>
            <label className="label mb-1">Data início (de)</label>
            <input
              type="datetime-local"
              className="input"
              value={filterDateFrom}
              onChange={e => setFilterDateFrom(e.target.value)}
            />
          </div>
          <div>
            <label className="label mb-1">Data início (até)</label>
            <input
              type="datetime-local"
              className="input"
              value={filterDateTo}
              onChange={e => setFilterDateTo(e.target.value)}
            />
          </div>
        </div>

        {/* Table */}
        <div className="card overflow-hidden">
          {/* On a narrow screen the list scrolls sideways rather than squeeze. */}
          <div className="overflow-x-auto">
          <div className="min-w-[760px]">
          {/* Table header */}
          <div className="grid grid-cols-7 px-5 py-3 text-xs font-semibold text-muted-foreground bg-muted/30 border-b border-border">
            <span className="col-span-2">Participante</span>
            <span className="col-span-2">Equipamento</span>
            <span>Estado</span>
            <span>Data Início</span>
            <span>Data Fim</span>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16 text-muted-foreground gap-2">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>A carregar…</span>
            </div>
          ) : error ? (
            <div className="px-5 py-10 text-center text-destructive text-sm">{error}</div>
          ) : assignments.length === 0 ? (
            <div className="px-5 py-16 text-center text-muted-foreground text-sm">
              <Search className="w-8 h-8 mx-auto mb-3 opacity-40" />
              Nenhum registo encontrado.
            </div>
          ) : (
            <div className="divide-y divide-border">
              {assignments.map(a => (
                <div
                  key={a.id}
                  className="grid grid-cols-7 px-5 py-3 items-center hover:bg-accent/30 transition-colors text-sm"
                >
                  <span className="col-span-2 font-medium text-foreground truncate pr-4">
                    {a.participantName}
                  </span>
                  <span className="col-span-2 text-foreground truncate pr-4">
                    {a.equipmentName}
                  </span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium w-fit ${STATUS_CLASSES[a.status]}`}>
                    {STATUS_LABELS[a.status]}
                  </span>
                  <span className="text-muted-foreground tabular-nums">{fmt(a.startedAt)}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground tabular-nums">{fmt(a.endedAt)}</span>
                    {/* Actions */}
                    <button
                      onClick={() => openEdit(a)}
                      title="Editar"
                      className="text-muted-foreground hover:text-primary transition-colors ml-auto"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    {a.status === 'confirmed' && (
                      <button
                        onClick={() => setCancelTarget(a)}
                        title="Cancelar"
                        className="text-muted-foreground hover:text-yellow-600 transition-colors"
                      >
                        <XCircle className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={() => setDeleteTarget(a)}
                      title="Eliminar"
                      className="text-muted-foreground hover:text-destructive transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
          </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-5 py-3 border-t border-border text-sm text-muted-foreground">
              <span>{total} registos · página {page + 1} de {totalPages}</span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => { setPage(p => p - 1); fetchList(page - 1); }}
                  disabled={page === 0}
                  className="p-1 rounded hover:bg-accent disabled:opacity-40 transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => { setPage(p => p + 1); fetchList(page + 1); }}
                  disabled={page >= totalPages - 1}
                  className="p-1 rounded hover:bg-accent disabled:opacity-40 transition-colors"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Create Modal ── */}
      {showCreate && (
        <Modal title="Nova Associação" onClose={() => setShowCreate(false)}>
          <AssignmentForm
            form={form}
            errors={formErrors}
            submitError={submitError}
            submitting={submitting}
            participants={participants}
            equipments={equipments}
            onChange={handleFormChange}
            onSubmit={handleCreate}
            onCancel={() => setShowCreate(false)}
            submitLabel="Criar"
          />
        </Modal>
      )}

      {/* ── Edit Modal ── */}
      {editTarget && (
        <Modal title={`Editar Associação #${editTarget.id}`} onClose={() => setEditTarget(null)}>
          <AssignmentForm
            form={form}
            errors={formErrors}
            submitError={submitError}
            submitting={submitting}
            participants={participants}
            equipments={equipments}
            onChange={handleFormChange}
            onSubmit={handleUpdate}
            onCancel={() => setEditTarget(null)}
            submitLabel="Guardar"
          />
        </Modal>
      )}

      {/* ── Cancel Confirm ── */}
      {cancelTarget && (
        <Modal title="Cancelar Associação" onClose={() => setCancelTarget(null)}>
          <p className="text-sm text-muted-foreground mb-6">
            Confirmas que pretendes cancelar a associação de{' '}
            <strong className="text-foreground">{cancelTarget.participantName}</strong> com{' '}
            <strong className="text-foreground">{cancelTarget.equipmentName}</strong>?
          </p>
          <div className="flex justify-end gap-3">
            <button onClick={() => setCancelTarget(null)} className="btn-ghost">Não</button>
            <button
              onClick={handleCancel}
              disabled={submitting}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-yellow-500 hover:bg-yellow-600 text-white text-sm font-medium transition-colors disabled:opacity-50"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              Cancelar Associação
            </button>
          </div>
        </Modal>
      )}

      {/* ── Delete Confirm ── */}
      {deleteTarget && (
        <Modal title="Eliminar Associação" onClose={() => setDeleteTarget(null)}>
          <p className="text-sm text-muted-foreground mb-6">
            Confirmas que pretendes eliminar permanentemente a associação de{' '}
            <strong className="text-foreground">{deleteTarget.participantName}</strong> com{' '}
            <strong className="text-foreground">{deleteTarget.equipmentName}</strong>?{' '}
            Esta acção não pode ser desfeita.
          </p>
          <div className="flex justify-end gap-3">
            <button onClick={() => setDeleteTarget(null)} className="btn-ghost">Não</button>
            <button
              onClick={handleDelete}
              disabled={submitting}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-destructive hover:bg-destructive/90 text-destructive-foreground text-sm font-medium transition-colors disabled:opacity-50"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              Eliminar
            </button>
          </div>
        </Modal>
      )}
    </>
  );
}

// ─── AssignmentForm ───────────────────────────────────────────────────────────

interface FormProps {
  form: AssignmentInput;
  errors: Record<string, string>;
  submitError: string;
  submitting: boolean;
  participants: ParticipantOption[];
  equipments: EquipmentOption[];
  onChange: (field: keyof AssignmentInput, value: string | number) => void;
  onSubmit: () => void;
  onCancel: () => void;
  submitLabel: string;
}

function AssignmentForm({
  form, errors, submitError, submitting,
  participants, equipments,
  onChange, onSubmit, onCancel, submitLabel,
}: FormProps) {
  return (
    <div className="space-y-4">
      {submitError && (
        <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-md">{submitError}</div>
      )}

      <div>
        <label className="label">Participante *</label>
        <select
          className={`input mt-1 ${errors.participantId ? 'border-destructive' : ''}`}
          value={form.participantId}
          onChange={e => onChange('participantId', e.target.value)}
        >
          <option value="">— Seleccionar —</option>
          {participants.map(p => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
        {errors.participantId && <p className="text-destructive text-xs mt-1">{errors.participantId}</p>}
      </div>

      <div>
        <label className="label">Equipamento *</label>
        <select
          className={`input mt-1 ${errors.equipmentId ? 'border-destructive' : ''}`}
          value={form.equipmentId}
          onChange={e => onChange('equipmentId', e.target.value)}
        >
          <option value="">— Seleccionar —</option>
          {equipments.map(e => (
            <option key={e.id} value={e.id}>{e.name}</option>
          ))}
        </select>
        {errors.equipmentId && <p className="text-destructive text-xs mt-1">{errors.equipmentId}</p>}
      </div>

      <div>
        <label className="label">Data/Hora de Início *</label>
        <input
          type="datetime-local"
          className={`input mt-1 ${errors.startedAt ? 'border-destructive' : ''}`}
          value={form.startedAt}
          onChange={e => onChange('startedAt', e.target.value)}
        />
        {errors.startedAt && <p className="text-destructive text-xs mt-1">{errors.startedAt}</p>}
      </div>

      <div>
        <label className="label">Data/Hora de Fim</label>
        <input
          type="datetime-local"
          className={`input mt-1 ${errors.endedAt ? 'border-destructive' : ''}`}
          value={form.endedAt}
          onChange={e => onChange('endedAt', e.target.value)}
        />
        {errors.endedAt && <p className="text-destructive text-xs mt-1">{errors.endedAt}</p>}
      </div>

      <div>
        <label className="label">Estado *</label>
        <select
          className={`input mt-1 ${errors.status ? 'border-destructive' : ''}`}
          value={form.status}
          onChange={e => onChange('status', e.target.value as AssignmentStatus)}
        >
          <option value="confirmed">Confirmado</option>
          <option value="cancelled">Cancelado</option>
          <option value="deleted">Eliminado</option>
        </select>
        {errors.status && <p className="text-destructive text-xs mt-1">{errors.status}</p>}
      </div>

      <div className="flex justify-end gap-3 pt-2">
        <button onClick={onCancel} type="button" className="btn-ghost">Cancelar</button>
        <button
          onClick={onSubmit}
          disabled={submitting}
          type="button"
          className="btn-primary flex items-center gap-2 disabled:opacity-50"
        >
          {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
          {submitLabel}
        </button>
      </div>
    </div>
  );
}
